'use client';
import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowLeft, ArrowRight, Check, Minus, Plus } from 'lucide-react';
import {
  CalendarOptions,
  calendarMonthNumbers,
  CUSTOM_SIZE_LIMITS,
  FORMAT_GROUPS,
  FORMAT_PRESETS,
  PRINT_SERVICE_IDS,
  printService,
  type PrintTarget,
  PAGE_COUNT_LIMITS,
  type FormatId,
  type PageSize,
} from '@freealbum/document';
import { CalendarSetup } from './calendar-setup';
import { THEMES } from '@freealbum/layouts';
import { Link, useRouter } from '@/i18n/navigation';
import { api } from '@/lib/api';
import { Button, Card, Input, SegmentedControl, Slider, toast } from '@freealbum/ui';
import { useApiErrorMessage } from '@/hooks/use-api-error';
import { LinkIcon } from '@/components/ui/link-status';
import { cn, clamp } from '@/lib/utils';

const STEPS = ['format', 'pages', 'details', 'theme'] as const;
type Step = (typeof STEPS)[number];

const PRESET_IDS = Object.keys(FORMAT_PRESETS) as Exclude<FormatId, 'custom'>[];
const FORMAT_IDS: FormatId[] = [...PRESET_IDS, 'custom'];
const MIN_CM = CUSTOM_SIZE_LIMITS.minMm / 10;
const MAX_CM = CUSTOM_SIZE_LIMITS.maxMm / 10;

/**
 * The unfinished setup, parked in the browser (UX-37).
 *
 * Four decisions stand between "new album" and the canvas, and until the POST lands none of them
 * exist anywhere else. A reload, a mis-tapped back gesture or a phone that drops the tab used to
 * throw all of it away. The key is versioned so a future shape change simply ignores old drafts
 * rather than trying to read them.
 */
const DRAFT_KEY = 'freealbum.wizard.v1';

interface Draft {
  projectType: 'album' | 'calendar';
  calendar: CalendarOptions;
  step: Step;
  format: FormatId;
  customCm: { w: string; h: string };
  innerPages: number;
  title: string;
  lang: 'he' | 'en';
  themeId: string;
  /** where the book will be printed; a shop narrows the sizes to the ones it sells */
  printService: PrintTarget['service'];
  binding: PrintTarget['binding'];
  /** a shop trim chosen from its list, to the tenth of a millimetre (the cm fields round to whole mm) */
  shopPage: { widthMm: number; heightMm: number } | null;
}

const PRINT_CHOICES: PrintTarget['service'][] = ['none', ...PRINT_SERVICE_IDS];

const makeDefaults = (locale: string): Draft => ({
  projectType: 'album',
  calendar: CalendarOptions.parse({ year: new Date().getFullYear() }),
  step: 'format',
  format: 'sq20',
  customCm: { w: '20', h: '20' },
  innerPages: 20,
  title: '',
  lang: locale === 'en' ? 'en' : 'he',
  themeId: 'family',
  printService: 'none',
  binding: 'hardcover',
  shopPage: null,
});

const sameDraft = (a: Draft, b: Draft): boolean =>
  a.projectType === b.projectType &&
  JSON.stringify(a.calendar) === JSON.stringify(b.calendar) &&
  a.step === b.step &&
  a.format === b.format &&
  a.printService === b.printService &&
  a.binding === b.binding &&
  a.shopPage?.widthMm === b.shopPage?.widthMm &&
  a.shopPage?.heightMm === b.shopPage?.heightMm &&
  a.customCm.w === b.customCm.w &&
  a.customCm.h === b.customCm.h &&
  a.innerPages === b.innerPages &&
  a.title === b.title &&
  a.lang === b.lang &&
  a.themeId === b.themeId;

/** Reads a draft defensively: anything unexpected falls back to the default for that field. */
function readDraft(defaults: Draft): Draft | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const d = parsed as Partial<Draft> & { customCm?: Partial<Draft['customCm']> };
    const cm: Partial<Draft['customCm']> = d.customCm ?? {};
    return {
      projectType: d.projectType === 'calendar' ? 'calendar' : 'album',
      calendar: CalendarOptions.safeParse(d.calendar).success ? CalendarOptions.parse(d.calendar) : defaults.calendar,
      step: STEPS.includes(d.step as Step) ? (d.step as Step) : defaults.step,
      format: FORMAT_IDS.includes(d.format as FormatId) ? (d.format as FormatId) : defaults.format,
      customCm: {
        w: typeof cm.w === 'string' ? cm.w.slice(0, 12) : defaults.customCm.w,
        h: typeof cm.h === 'string' ? cm.h.slice(0, 12) : defaults.customCm.h,
      },
      innerPages:
        typeof d.innerPages === 'number' && Number.isFinite(d.innerPages)
          ? clamp(Math.round(d.innerPages / 2) * 2, PAGE_COUNT_LIMITS.min, PAGE_COUNT_LIMITS.max)
          : defaults.innerPages,
      title: typeof d.title === 'string' ? d.title.slice(0, 200) : defaults.title,
      lang: d.lang === 'he' || d.lang === 'en' ? d.lang : defaults.lang,
      themeId: THEMES.some((t) => t.id === d.themeId) ? (d.themeId as string) : defaults.themeId,
      printService: PRINT_CHOICES.includes(d.printService as PrintTarget['service']) ? (d.printService as PrintTarget['service']) : defaults.printService,
      binding: d.binding === 'softcover' || d.binding === 'hardcover' ? d.binding : defaults.binding,
      shopPage:
        d.shopPage && typeof d.shopPage === 'object' && Number.isFinite(d.shopPage.widthMm) && Number.isFinite(d.shopPage.heightMm)
          ? { widthMm: d.shopPage.widthMm, heightMm: d.shopPage.heightMm }
          : defaults.shopPage,
    };
  } catch {
    return null;
  }
}

/** Millimetres as centimetres for a label, to the tenth of a millimetre and without float noise: 241.3 → "24.13". */
const cmLabel = (mm: number): string => (mm / 10).toFixed(3).replace(/\.?0+$/, '');

// ── custom size arithmetic (UX-35) ────────────────────────────────────────────
/**
 * Fields hold what was typed, not a number.
 *
 * `type="number"` throws away a partially typed value ("20." reads back as ""), which made the
 * field jump under the user's fingers; and `Number('')` is 0, which used to be silently clamped up
 * to the minimum. Text plus an explicit parse keeps "what I typed" and "what will be built" apart.
 */
function parseCm(raw: string): number | null {
  const s = raw.trim().replace(',', '.');
  if (!s || !/^\d+(\.\d+)?$|^\.\d+$|^\d+\.$/.test(s)) return null;
  const n = Number(s.endsWith('.') ? s.slice(0, -1) : s);
  return Number.isFinite(n) ? n : null;
}

/** cm → mm at the precision the document actually stores: whole millimetres. */
const mmFromCm = (cm: number): number => Math.round(cm * 10);
/** The exact number to print next to the field, derived from the mm that will be saved. */
const cmFromMm = (mm: number): number => mm / 10;

type DimError = 'empty' | 'range' | null;

function dimError(raw: string, value: number | null): DimError {
  if (!raw.trim()) return 'empty';
  if (value === null) return 'range';
  const mm = mmFromCm(value);
  return mm < CUSTOM_SIZE_LIMITS.minMm || mm > CUSTOM_SIZE_LIMITS.maxMm ? 'range' : null;
}

/**
 * One option in a pick-exactly-one group.
 *
 * A real `<input type="radio">` rather than a button with `aria-pressed`: "pressed" describes a
 * toggle, and a screen reader should say "selected, 2 of 9" here. The native input also brings
 * arrow-key navigation and group semantics for free. It is visually hidden, so the tick badge is
 * what the eye actually reads — a border-and-tint change alone left people unsure whether their
 * tap had registered.
 */
function RadioCard({
  name,
  checked,
  onSelect,
  className,
  children,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label
      className={cn(
        'relative cursor-pointer rounded-lg border transition-colors duration-(--duration-fast)',
        'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus',
        checked ? 'border-accent bg-accent-soft' : 'border-line bg-surface hover:border-line-strong',
        className,
      )}
    >
      {/*
        Transparent rather than `sr-only`, and stretched over the whole card: the input itself is
        the hit target, so the entire card is tappable and the control a pointer lands on is the
        one assistive tech and tests address. A visually-hidden input is unclickable.
      */}
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        className="absolute inset-0 z-10 m-0 cursor-pointer appearance-none rounded-lg opacity-0"
      />
      <span
        aria-hidden
        className={cn(
          'absolute top-2 grid size-5 place-items-center rounded-full border end-2',
          checked ? 'border-accent bg-accent text-accent-fg' : 'border-line-strong bg-surface',
        )}
      >
        {checked ? <Check className="size-3" strokeWidth={3} /> : null}
      </span>
      {children}
    </label>
  );
}

/** Format tile: a to-scale rectangle so the shape is legible before any preview exists. */
function FormatTile({
  widthMm,
  heightMm,
  label,
  selected,
  onSelect,
}: {
  widthMm: number;
  heightMm: number;
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const max = Math.max(widthMm, heightMm);
  return (
    <RadioCard name="album-format" checked={selected} onSelect={onSelect} className="flex flex-col items-center gap-2 p-3">
      <span className="grid h-20 w-full place-items-center">
        <span
          aria-hidden
          className={cn('block rounded-xs border shadow-xs', selected ? 'border-accent bg-surface-2' : 'border-line-strong bg-surface-2')}
          style={{ width: `${(widthMm / max) * 72}px`, height: `${(heightMm / max) * 72}px` }}
        />
      </span>
      <span className="text-center text-xs leading-tight text-ink-2">{label}</span>
    </RadioCard>
  );
}

export function NewAlbumWizard() {
  const t = useTranslations('newAlbum');
  const tf = useTranslations('formats');
  const tt = useTranslations('themes');
  const tc = useTranslations('common');
  const locale = useLocale();
  const router = useRouter();
  const errorMessage = useApiErrorMessage();

  const defaults = React.useMemo(() => makeDefaults(locale), [locale]);

  const [projectType, setProjectType] = React.useState<Draft['projectType']>(defaults.projectType);
  const [calendar, setCalendar] = React.useState(defaults.calendar);
  const isCalendar = projectType === 'calendar';
  const calendarValid = CalendarOptions.safeParse(calendar).success;
  const calendarMonths = calendarValid ? calendarMonthNumbers(calendar).length : 12;
  const [step, setStep] = React.useState<Step>(defaults.step);
  const [format, setFormat] = React.useState<FormatId>(defaults.format);
  const [customCm, setCustomCm] = React.useState(defaults.customCm);
  const [innerPages, setInnerPages] = React.useState(defaults.innerPages);
  const actualPages = isCalendar ? Math.ceil(calendarMonths / 2) * 2 : innerPages;
  const [title, setTitle] = React.useState(defaults.title);
  const [lang, setLang] = React.useState<'he' | 'en'>(defaults.lang);
  const [themeId, setThemeId] = React.useState(defaults.themeId);
  const [printAt, setPrintAt] = React.useState<PrintTarget['service']>(defaults.printService);
  const [binding, setBinding] = React.useState<PrintTarget['binding']>(defaults.binding);
  const [shopPage, setShopPage] = React.useState<Draft['shopPage']>(defaults.shopPage);
  const [creating, setCreating] = React.useState(false);
  /** Errors appear once a field has been left, or once Next has been refused — never mid-keystroke. */
  const [touched, setTouched] = React.useState({ w: false, h: false });
  const [restored, setRestored] = React.useState(false);

  const widthRef = React.useRef<HTMLInputElement>(null);
  const heightRef = React.useRef<HTMLInputElement>(null);
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const hydrated = React.useRef(false);
  const headingIds = React.useId();

  const Forward = locale === 'he' ? ArrowLeft : ArrowRight;
  const Backward = locale === 'he' ? ArrowRight : ArrowLeft;

  // ── draft: restore once, then mirror every change ───────────────────────────
  React.useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const draft = readDraft(defaults);
    if (!draft || sameDraft(draft, defaults)) return;
    setProjectType(draft.projectType);
    setCalendar(draft.calendar);
    setStep(draft.step);
    setFormat(draft.format);
    setCustomCm(draft.customCm);
    setInnerPages(draft.innerPages);
    setTitle(draft.title);
    setLang(draft.lang);
    setThemeId(draft.themeId);
    setPrintAt(draft.printService);
    setBinding(draft.binding);
    setShopPage(draft.shopPage);
    setRestored(true);
  }, [defaults]);

  React.useEffect(() => {
    if (!hydrated.current) return;
    const draft: Draft = { projectType, calendar, step, format, customCm, innerPages, title, lang, themeId, printService: printAt, binding, shopPage };
    try {
      // Nothing decided yet is nothing worth restoring — and nothing worth announcing on return.
      if (sameDraft(draft, defaults)) window.localStorage.removeItem(DRAFT_KEY);
      else window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      /* private mode, full quota: the wizard still works, it just cannot remember. */
    }
  }, [defaults, projectType, calendar, step, format, customCm, innerPages, title, lang, themeId, printAt, binding, shopPage]);

  const clearDraft = React.useCallback(() => {
    try {
      window.localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const discardDraft = () => {
    clearDraft();
    setProjectType(defaults.projectType);
    setCalendar(defaults.calendar);
    setPrintAt(defaults.printService);
    setBinding(defaults.binding);
    setShopPage(defaults.shopPage);
    setStep(defaults.step);
    setFormat(defaults.format);
    setCustomCm(defaults.customCm);
    setInnerPages(defaults.innerPages);
    setTitle(defaults.title);
    setLang(defaults.lang);
    setThemeId(defaults.themeId);
    setTouched({ w: false, h: false });
    setRestored(false);
  };

  // ── custom size ─────────────────────────────────────────────────────────────
  const wValue = parseCm(customCm.w);
  const hValue = parseCm(customCm.h);
  const errors = format === 'custom' ? { w: dimError(customCm.w, wValue), h: dimError(customCm.h, hValue) } : { w: null, h: null };
  const sizeInvalid = Boolean(errors.w ?? errors.h);

  /** The page that will actually be created — the single source for both the POST and the summary. */
  const shop = printService(printAt);
  const shopTrim = shop && shopPage && String(shopPage.widthMm / 10) === customCm.w && String(shopPage.heightMm / 10) === customCm.h ? shopPage : null;
  const customPage: PageSize | null =
    format === 'custom' && !sizeInvalid && wValue !== null && hValue !== null
      ? (shopTrim ?? { widthMm: mmFromCm(wValue), heightMm: mmFromCm(hValue) })
      : null;

  const resolvedPage: PageSize | null =
    format === 'custom'
      ? customPage
      : { widthMm: FORMAT_PRESETS[format as Exclude<FormatId, 'custom'>].widthMm, heightMm: FORMAT_PRESETS[format as Exclude<FormatId, 'custom'>].heightMm };

  const errorText = (which: 'w' | 'h'): string | null => {
    const err = errors[which];
    if (!err || !touched[which]) return null;
    return err === 'empty' ? t('format.errorEmpty', { min: MIN_CM, max: MAX_CM }) : t('format.errorRange', { min: MIN_CM, max: MAX_CM });
  };

  /** "20.55 cm" is a real request, but the document is built in whole millimetres — so say so. */
  const hintText = (which: 'w' | 'h'): string => {
    const value = which === 'w' ? wValue : hValue;
    if (value !== null && !errors[which] && mmFromCm(value) !== value * 10) {
      return t('format.rounded', { value: cmFromMm(mmFromCm(value)) });
    }
    return t('format.range', { min: MIN_CM, max: MAX_CM });
  };

  const stepIndex = STEPS.indexOf(step);
  const te = useTranslations('export');

  /** Refuses to leave the size step while a side is empty or out of range, and says which. */
  const revealSizeProblem = (): boolean => {
    if (step !== 'format' || !sizeInvalid) return false;
    setTouched({ w: true, h: true });
    const target = errors.w ? widthRef : heightRef;
    target.current?.focus();
    target.current?.select();
    return true;
  };

  const goNext = () => {
    if (revealSizeProblem() || (step === 'pages' && isCalendar && !calendarValid)) return;
    setStep(STEPS[Math.min(STEPS.length - 1, stepIndex + 1)]!);
  };
  const goBack = () => setStep(STEPS[Math.max(0, stepIndex - 1)]!);

  // Keyboard and screen-reader users get moved to the new step's heading; the first render is left
  // alone so arriving on the page does not yank focus away from the browser chrome.
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  const create = async () => {
    if (isCalendar && !calendarValid) { setStep('pages'); return; }
    if (format === 'custom' && !customPage) {
      setStep('format');
      setTouched({ w: true, h: true });
      return;
    }
    setCreating(true);
    try {
      const { album } = await api.albums.create({
        format,
        ...(customPage ? { page: customPage } : {}),
        innerPages: actualPages,
        ...(isCalendar ? { calendar } : {}),
        title: title.trim(),
        lang,
        themeId,
        ...(printAt !== 'none' ? { print: { service: printAt, binding } } : {}),
      });
      // The album now exists on the server; the local copy of the setup has done its job.
      clearDraft();
      router.replace(`/albums/${album.id}/edit`);
    } catch (err) {
      toast({ title: t('createFailed'), description: errorMessage(err), variant: 'error' });
      setCreating(false);
    }
  };

  const stepPages = (delta: number) => setInnerPages((n) => clamp(n + delta, PAGE_COUNT_LIMITS.min, PAGE_COUNT_LIMITS.max));

  const headingId = (s: Step) => `${headingIds}-${s}`;
  const headingClass = 'font-display text-lg text-ink focus:outline-none';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-display text-2xl text-ink">{t('title')}</h1>
          {/* An unfinished album is not a trap: the way back to the list is always on screen. */}
          <Button asChild variant="ghost" size="sm" startIcon={<LinkIcon><Backward aria-hidden /></LinkIcon>}>
            <Link href="/albums">{t('backToAlbums')}</Link>
          </Button>
        </div>

        <div className="mt-4 flex flex-col gap-2 rounded-lg border border-line bg-surface p-4">
          <span className="text-sm font-medium text-ink">{t('calendar.projectType')}</span>
          <SegmentedControl aria-label={t('calendar.projectType')} value={projectType}
            onValueChange={(value: Draft['projectType']) => {
              if (value === projectType) return;
              setProjectType(value);
              if (value === 'calendar') {
                if (step === 'format') setFormat('a4p');
                setPrintAt('none');
              }
              // A restored draft may open on Details or Style. Show the new type's setup now.
              if (step !== 'format') setStep('pages');
            }}
            options={[{ value: 'album', label: t('calendar.photoAlbum'), disabled: creating }, { value: 'calendar', label: t('calendar.photoCalendar'), disabled: creating }]} fullWidth />
        </div>

        {/* Narrow phones get the position as one short line; the tappable trail needs room to read. */}
        <p className="mt-4 text-sm text-ink-2 sm:hidden">
          <span className="font-medium text-ink">{t('stepOf', { current: stepIndex + 1, total: STEPS.length })}</span>
          {' · '}
          {isCalendar && step === 'pages' ? t('calendar.title') : t(`steps.${step}`)}
        </p>
        <ol className="mt-4 hidden flex-wrap items-center gap-2 sm:flex" aria-label={t('title')}>
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => i <= stepIndex && setStep(s)}
                disabled={i > stepIndex}
                aria-current={s === step ? 'step' : undefined}
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors',
                  s === step ? 'bg-accent text-accent-fg' : i < stepIndex ? 'bg-accent-soft text-accent' : 'bg-paper-2 text-ink-3',
                )}
              >
                {i < stepIndex ? <Check aria-hidden className="size-3" /> : <span className="tabular-nums">{i + 1}</span>}
                {isCalendar && s === 'pages' ? t('calendar.title') : t(`steps.${s}`)}
              </button>
              {i < STEPS.length - 1 ? <span aria-hidden className="text-ink-3">·</span> : null}
            </li>
          ))}
        </ol>
      </div>

      {restored ? (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-paper-2 px-3 py-2 text-sm text-ink-2"
        >
          <span>{t('draft.restored')}</span>
          <Button variant="ghost" size="sm" onClick={discardDraft}>
            {t('draft.discard')}
          </Button>
        </div>
      ) : null}

      <Card className="p-5">
        {step === 'format' ? (
          <section className="flex flex-col gap-4" aria-labelledby={headingId('format')}>
            <div>
              <h2 id={headingId('format')} ref={headingRef} tabIndex={-1} className={headingClass}>
                {t('format.title')}
              </h2>
              <p className="mt-1 text-sm text-ink-2">{t('format.lead')}</p>
            </div>
            <div className="flex flex-col gap-2 rounded-md border border-line bg-paper-2 p-3">
              <span className="text-sm font-medium text-ink">{t('format.printAt')}</span>
              <SegmentedControl
                aria-label={t('format.printAt')}
                size="sm"
                value={printAt}
                onValueChange={(v) => setPrintAt(v as PrintTarget['service'])}
                options={PRINT_CHOICES.map((id) => ({ value: id, label: id === 'none' ? t('format.printAnywhere') : te(`services.${id}.name`) }))}
                fullWidth
              />
              {shop ? (
                <SegmentedControl
                  aria-label={t('format.bookBinding')}
                  size="sm"
                  value={binding}
                  onValueChange={(v) => setBinding(v as PrintTarget['binding'])}
                  options={[
                    { value: 'hardcover', label: t('format.hardcover') },
                    { value: 'softcover', label: t('format.softcover') },
                  ]}
                  fullWidth
                />
              ) : null}
              <span className="text-xs text-ink-3">{shop ? t('format.printShopHint', { service: te(`services.${shop.id}.name`) }) : t('format.printHint')}</span>
            </div>

            {shop ? (
              <div role="radiogroup" aria-label={t('format.title')} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {shop.sizes.map((size) => {
                  const cm = { w: String(size.widthMm / 10), h: String(size.heightMm / 10) };
                  const selected = format === 'custom' && customCm.w === cm.w && customCm.h === cm.h;
                  return (
                    <FormatTile
                      key={`${size.widthMm}x${size.heightMm}`}
                      widthMm={size.widthMm}
                      heightMm={size.heightMm}
                      label={t('format.serviceSize', { w: size.widthIn, h: size.heightIn, wcm: cmLabel(size.widthMm), hcm: cmLabel(size.heightMm) })}
                      selected={selected}
                      onSelect={() => {
                        setFormat('custom');
                        setCustomCm(cm);
                        setShopPage({ widthMm: size.widthMm, heightMm: size.heightMm });
                        setTouched({ w: false, h: false });
                      }}
                    />
                  );
                })}
              </div>
            ) : null}

            <div role="radiogroup" aria-label={t('format.title')} className="flex flex-col gap-4" hidden={shop !== null}>
              {FORMAT_GROUPS.map((group) => (
                <div key={group} className="flex flex-col gap-2">
                  <h3 className="text-xs font-medium text-ink-2">{t(`format.group.${group}`)}</h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {PRESET_IDS.filter((id) => FORMAT_PRESETS[id].group === group).map((id) => {
                      const preset = FORMAT_PRESETS[id];
                      return (
                        <FormatTile
                          key={id}
                          widthMm={preset.widthMm}
                          heightMm={preset.heightMm}
                          label={tf(id)}
                          selected={format === id}
                          onSelect={() => setFormat(id)}
                        />
                      );
                    })}
                    {group === 'inch' ? (
                      <FormatTile
                        widthMm={customPage?.widthMm ?? 200}
                        heightMm={customPage?.heightMm ?? 200}
                        label={tf('custom')}
                        selected={format === 'custom'}
                        onSelect={() => setFormat('custom')}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {format === 'custom' && !shop ? (
              <fieldset className="flex flex-col gap-3 rounded-md border border-line bg-paper-2 p-4">
                <legend className="px-1 text-sm font-medium text-ink-2">{t('format.customTitle')}</legend>
                <div className="flex flex-wrap gap-3">
                  <Input
                    ref={widthRef}
                    label={t('format.width')}
                    containerClassName="min-w-0 grow basis-32"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    dir="ltr"
                    className="text-end"
                    value={customCm.w}
                    hint={hintText('w')}
                    error={errorText('w')}
                    onChange={(e) => setCustomCm((c) => ({ ...c, w: e.target.value }))}
                    onBlur={() => setTouched((s) => ({ ...s, w: true }))}
                  />
                  <Input
                    ref={heightRef}
                    label={t('format.height')}
                    containerClassName="min-w-0 grow basis-32"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    dir="ltr"
                    className="text-end"
                    value={customCm.h}
                    hint={hintText('h')}
                    error={errorText('h')}
                    onChange={(e) => setCustomCm((c) => ({ ...c, h: e.target.value }))}
                    onBlur={() => setTouched((s) => ({ ...s, h: true }))}
                  />
                </div>
                <p className="text-xs text-ink-3">{t('format.limits', { min: MIN_CM, max: MAX_CM })}</p>
              </fieldset>
            ) : null}
          </section>
        ) : null}

        {step === 'pages' ? (
          <section className="flex flex-col gap-4" aria-labelledby={headingId('pages')}>
            <div>
              <h2 id={headingId('pages')} ref={headingRef} tabIndex={-1} className={headingClass}>
                {isCalendar ? t('calendar.title') : t('pages.title')}
              </h2>
              {!isCalendar ? <p className="mt-1 text-sm text-ink-2">{t('pages.lead', { min: PAGE_COUNT_LIMITS.min, max: PAGE_COUNT_LIMITS.max })}</p> : null}
            </div>
            {isCalendar ? (
              <CalendarSetup value={calendar} onChange={setCalendar} />
            ) : (
              <>
                {/*
                  The count takes the leftover width instead of reserving 10rem of it: at 320 px the old
                  fixed centre pushed both 44 px buttons past the card's edge and clipped one of them.
                */}
                <div className="mx-auto flex w-full max-w-sm items-center justify-center gap-3">
                  <Button
                    variant="secondary"
                    className="w-touch shrink-0 px-0"
                    onClick={() => stepPages(-2)}
                    disabled={innerPages <= PAGE_COUNT_LIMITS.min}
                    aria-label={t('pages.decrease')}
                  >
                    <Minus aria-hidden className="size-5" />
                  </Button>
                  <div className="min-w-0 flex-1 text-center">
                    <p className="font-display text-4xl tabular-nums text-ink">{innerPages}</p>
                    <p className="text-xs text-ink-3">{t('pages.spreads', { count: innerPages / 2 + 1 })}</p>
                  </div>
                  <Button
                    variant="secondary"
                    className="w-touch shrink-0 px-0"
                    onClick={() => stepPages(2)}
                    disabled={innerPages >= PAGE_COUNT_LIMITS.max}
                    aria-label={t('pages.increase')}
                  >
                    <Plus aria-hidden className="size-5" />
                  </Button>
                </div>
                <Slider
                  min={PAGE_COUNT_LIMITS.min}
                  max={PAGE_COUNT_LIMITS.max}
                  step={2}
                  value={innerPages}
                  aria-label={t('pages.title')}
                  onValueChange={setInnerPages}
                  formatValue={(v) => t('pages.value', { count: v })}
                />
              </>
            )}
          </section>
        ) : null}

        {step === 'details' ? (
          <section className="flex flex-col gap-4" aria-labelledby={headingId('details')}>
            <div>
              <h2 id={headingId('details')} ref={headingRef} tabIndex={-1} className={headingClass}>
                {t('details.title')}
              </h2>
              <p className="mt-1 text-sm text-ink-2">{t('details.lead')}</p>
            </div>
            <Input
              label={t('details.titleLabel')}
              value={title}
              maxLength={200}
              placeholder={t('details.titlePlaceholder')}
              onChange={(e) => setTitle(e.target.value)}
              className="k-nikud"
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('details.langLabel')}</span>
              <SegmentedControl
                aria-label={t('details.langLabel')}
                value={lang}
                onValueChange={setLang}
                options={[
                  { value: 'he', label: t('details.langHe') },
                  { value: 'en', label: t('details.langEn') },
                ]}
              />
              <span className="text-xs text-ink-3">{t('details.langHint')}</span>
            </div>
          </section>
        ) : null}

        {step === 'theme' ? (
          <section className="flex flex-col gap-4" aria-labelledby={headingId('theme')}>
            <div>
              <h2 id={headingId('theme')} ref={headingRef} tabIndex={-1} className={headingClass}>
                {t('theme.title')}
              </h2>
              <p className="mt-1 text-sm text-ink-2">{t('theme.lead')}</p>
            </div>
            <div role="radiogroup" aria-label={t('theme.title')} className="grid gap-3 sm:grid-cols-2">
              {THEMES.map((theme) => (
                <RadioCard
                  key={theme.id}
                  name="album-theme"
                  checked={themeId === theme.id}
                  onSelect={() => setThemeId(theme.id)}
                  className="flex flex-col gap-3 p-4 text-start"
                >
                  {/* Padded away from the tick badge so the swatches never sit under it. */}
                  <span className="flex gap-1.5 pe-7" aria-hidden>
                    {theme.palette.map((c) => (
                      <span key={c} className="size-7 rounded-full border border-line-strong" style={{ background: c }} />
                    ))}
                  </span>
                  <span>
                    <span className="block font-display text-base text-ink">{tt(theme.id as 'family')}</span>
                    <span className="block text-xs text-ink-2">{tt(`${theme.id}Desc` as 'familyDesc')}</span>
                  </span>
                  <span className="text-2xs text-ink-3" dir="ltr">
                    {theme.headingFont} · {theme.bodyFont}
                  </span>
                </RadioCard>
              ))}
            </div>

            {/*
              The last thing read before the album exists, and the same numbers the POST carries —
              the size here is derived from the page object itself, not from what was typed.
            */}
            <dl className="grid gap-1.5 rounded-md border border-line bg-paper-2 p-3 text-sm" aria-label={t('summary.title')}>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-3">{t('summary.size')}</dt>
                <dd className="text-ink">
                  {resolvedPage
                    ? t('format.sizeLabel', { w: cmFromMm(resolvedPage.widthMm), h: cmFromMm(resolvedPage.heightMm) })
                    : t('summary.missingSize')}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-3">{t('summary.pages')}</dt>
                <dd className="text-ink tabular-nums">{t('pages.value', { count: actualPages })}</dd>
              </div>
              {isCalendar ? <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-3">{t('calendar.title')}</dt>
                <dd className="text-ink">{t(`calendar.${calendar.system}`)} · {calendar.year} · {t(`calendar.${calendar.region}`)}</dd>
              </div> : null}
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-3">{t('summary.name')}</dt>
                <dd className="min-w-0 truncate text-ink">{title.trim() || t('summary.untitled')}</dd>
              </div>
            </dl>
          </section>
        ) : null}
      </Card>

      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={goBack} disabled={stepIndex === 0} startIcon={<Backward aria-hidden />}>
          {tc('back')}
        </Button>
        {step === 'theme' ? (
          <Button variant="primary" size="lg" onClick={() => void create()} loading={creating}>
            {creating ? t('creating') : t('create')}
          </Button>
        ) : (
          <Button variant="primary" onClick={goNext} endIcon={<Forward aria-hidden />}>
            {tc('next')}
          </Button>
        )}
      </div>
    </div>
  );
}
